import React from 'react';
import ProToolsTrackRow from './ProToolsTrackRow';

const VOCAL_TRACK_TYPES = [
  { id: 'lead', name: 'Lead', color: '#ef4444' },
  { id: 'double', name: 'Double', color: '#f97316' },
  { id: 'adlib', name: 'Adlib', color: '#6366f1' },
  { id: 'extra', name: 'Extra', color: '#22c55e' },
];

const ProToolsEditWindow = ({
  beatInfo,
  isBeatPlaying,
  onBeatPlayPause,
  sections,
  armedChannel,
  auditionChannels,
  onArm,
  onMute,
  onSolo,
  onAudition,
  onPlayback,
  onDelete,
  onPanChange,
  onVolumeChange,
  onClipChange,
  onSelectTake,
  onSeek,
  onBeginEdit,
  recordedWaveforms,
  takes,
  timelineDuration,
  currentTime,
  snapEnabled,
  snapSeconds,
  disabled,
}) => {
  const quantize = (value) => {
    const grid = Number(snapSeconds) || 0;
    if (!snapEnabled || grid <= 0) return value;
    return Math.max(0, Math.round(value / grid) * grid);
  };
  const visibleDuration = Math.max(Number(timelineDuration) || 0, 1);
  const bpmValue = Math.max(40, Number(beatInfo?.bpm) || 120);
  const beatSeconds = 60 / bpmValue;
  const barSeconds = beatSeconds * 4;
  const totalBars = Math.max(1, Math.ceil(visibleDuration / barSeconds));
  const labelEveryBars = Math.max(1, Math.ceil(totalBars / 12));
  const barLines = Array.from({ length: totalBars + 1 }, (_, index) => ({
    pct: Math.max(0, Math.min(100, ((index * barSeconds) / visibleDuration) * 100)),
    label: index + 1,
    major: true,
    showLabel: index % labelEveryBars === 0,
  }));
  const snapGridSeconds = Math.max(0.01, Number(snapSeconds) || beatSeconds);
  const snapLineCount = snapEnabled ? Math.min(160, Math.floor(visibleDuration / snapGridSeconds)) : 0;
  const snapLines = Array.from({ length: snapLineCount + 1 }, (_, index) => ({
    pct: Math.max(0, Math.min(100, ((index * snapGridSeconds) / visibleDuration) * 100)),
    major: false,
  }));
  const gridLines = [
    ...snapLines,
    ...barLines,
  ];
  const playheadPct = Math.max(0, Math.min(100, ((Number(currentTime) || 0) / visibleDuration) * 100));
  const handleLaneSeek = (event) => {
    const rect = event.currentTarget?.getBoundingClientRect();
    if (!rect?.width) return;
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    onSeek?.(quantize(ratio * visibleDuration));
  };

  return (
    <div
      className="rounded-lg overflow-hidden border border-gray-800 shadow-[0_12px_32px_rgba(0,0,0,0.85)]"
      style={{ background: '#0b0b10' }}
    >
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#050509] border-b border-gray-900">
        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-[0.2em]">
          Edit Window — Vocal Tracks
        </span>
        <span className="text-[9px] font-mono text-gray-600">
          R = Record · I = Monitor · S = Solo · M = Mute
        </span>
      </div>

      <div className="flex items-stretch border-b border-gray-900 bg-[#07070c]">
        <div
          className="flex-shrink-0 px-2 py-1 border-r border-gray-900"
          style={{ width: 170, background: 'rgba(15,15,20,0.98)' }}
        >
          <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-gray-600">Bars</span>
        </div>
        <div
          className="relative h-7 flex-1 cursor-crosshair"
          onMouseDown={handleLaneSeek}
          title="Click ruler to move playhead"
        >
          {(gridLines || []).map((line, index) => (
            <div
              key={`ruler-line-${index}-${line.pct}`}
              className={`absolute top-0 bottom-0 ${line.major ? 'bg-gray-500/35' : 'bg-gray-700/20'}`}
              style={{ left: `${line.pct}%`, width: line.major ? 1 : 0.5 }}
            />
          ))}
          {barLines.map((line) => (
            line.showLabel && (
              <span
                key={`bar-${line.label}`}
                className="absolute top-1 text-[9px] font-mono text-gray-500"
                style={{ left: `${line.pct}%`, transform: 'translateX(4px)' }}
              >
                {line.label}
              </span>
            )
          ))}
          <div
            className="absolute top-0 bottom-0 z-10 w-px bg-cyan-300 shadow-[0_0_10px_rgba(103,232,249,0.9)] pointer-events-none"
            style={{ left: `${playheadPct}%` }}
          >
            <span className="absolute -top-0.5 left-1 h-2 w-2 -translate-x-1/2 rotate-45 bg-cyan-300" />
          </div>
        </div>
      </div>

      <div
        className="flex items-stretch border-b border-gray-900"
        style={{ minHeight: 54, background: 'rgba(10,10,15,0.96)' }}
      >
        <div
          className="flex-shrink-0 flex flex-col justify-between px-2 py-1.5 border-r border-gray-900"
          style={{ width: 170, background: 'rgba(15,15,20,0.98)' }}
        >
          <div className="flex items-center gap-1.5">
            <div
              className="w-1.5 h-4 rounded-sm flex-shrink-0"
              style={{ background: '#facc15' }}
            />
            <span className="text-[11px] font-mono font-semibold text-yellow-300 truncate">
              {beatInfo?.name || 'Beat Track'}
            </span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-center gap-1">
              <span className="text-[9px] font-mono text-gray-600 mr-1">Beat</span>
              <button
                onClick={onBeatPlayPause}
                disabled={!beatInfo}
                className={`w-6 h-5 rounded text-[9px] font-bold font-mono border transition-all disabled:opacity-40 ${
                  isBeatPlaying
                    ? 'bg-emerald-600 border-emerald-500 text-white'
                    : 'bg-gray-900 border-gray-700 text-gray-400 hover:bg-emerald-900/40 hover:border-emerald-600'
                }`}
                title={isBeatPlaying ? 'Pause beat' : 'Play beat'}
              >
                {isBeatPlaying ? '⏸' : '▶'}
              </button>
            </div>
            {beatInfo && (
              <span className="text-[9px] font-mono text-gray-500">
                {beatInfo?.bpm ? `${beatInfo?.bpm} BPM` : ''}
              </span>
            )}
          </div>
        </div>

        <div
          className="flex-1 relative flex items-center px-3 cursor-crosshair"
          onMouseDown={handleLaneSeek}
          title="Click to move playhead"
        >
          <div className="absolute inset-0 pointer-events-none">
            {(gridLines || []).map((line, index) => (
              <div
                key={`beat-grid-${index}-${line.pct}`}
                className={`absolute top-0 bottom-0 ${line.major ? 'bg-gray-600/30' : 'bg-gray-700/15'}`}
                style={{
                  left: `${line.pct}%`,
                  width: line.major ? 1 : 0.5,
                }}
              />
            ))}
          </div>
          {beatInfo ? (
            <div className="w-full h-8 flex items-center gap-0.5">
              {Array.from({ length: 90 })?.map((_, i) => {
                const h =
                  20 + Math.sin(i * 0.7) * 10 + (i % 3 === 0 ? 6 : 2);
                return (
                  <div
                    key={i}
                    className="flex-1 rounded-sm"
                    style={{
                      height: `${h}px`,
                      background: isBeatPlaying ? '#facc15' : '#78716c',
                      opacity: isBeatPlaying ? 0.85 : 0.65,
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <div className="w-full flex items-center justify-center">
              <span className="text-[10px] font-mono text-gray-600">
                Import a beat to start recording.
              </span>
            </div>
          )}
          <div
            className="absolute top-0 bottom-0 z-10 w-px bg-cyan-300 shadow-[0_0_10px_rgba(103,232,249,0.9)] pointer-events-none"
            style={{ left: `${playheadPct}%` }}
          >
            <span className="absolute -top-1 left-1 h-2 w-2 -translate-x-1/2 rotate-45 bg-cyan-300" />
          </div>
        </div>
      </div>

      {sections?.map((section) => (
        <div key={section?.key || section?.id}>
          <div
            className="flex items-center px-3 py-1 border-b border-gray-900"
            style={{ background: 'rgba(18,18,26,0.96)' }}
          >
            <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-gray-500">
              {section?.label}
            </span>
          </div>

          {VOCAL_TRACK_TYPES?.map((type) => {
            const channel = section?.channels?.find((c) => c?.id === type?.id);
            const sectionId = section?.key || section?.id;
            const channelKey = `${sectionId}-${type?.id}`;
            const wfKey = `${section?.key || section?.id}-${type?.id}`;
            const wfData = recordedWaveforms?.[wfKey] || null;
            const channelTakes = takes?.[channelKey] || [];
            const activeTakes = channelTakes.filter((take) => take?.timelineActive === true);
            const selectedOrFallback = channelTakes.find((take) => take?.id === channel?.selectedTakeId)
              || channelTakes.find((take) => take?.isSelected)
              || null;
            const timelineTakes = activeTakes.length ? activeTakes : (selectedOrFallback ? [selectedOrFallback] : []);
            const isArmed =
              armedChannel?.sectionId === sectionId &&
              armedChannel?.channelId === type?.id;
            const isAudition = auditionChannels?.[channelKey] || false;

            return (
              <ProToolsTrackRow
                key={type?.id}
                trackName={type?.name}
                trackColor={type?.color}
                isArmed={isArmed}
                isMuted={channel?.muted || false}
                isSoloed={channel?.soloed || false}
                isAudition={isAudition}
                isRecording={channel?.isRecording || false}
                isPlaying={channel?.isPlaying || false}
                hasRecording={channel?.recorded || false}
                waveformData={wfData}
                timelineTakes={timelineTakes}
                selectedTakeId={channel?.selectedTakeId}
                duration={channel?.duration}
                clipStart={channel?.clipStart ?? 0}
                clipEnd={channel?.clipEnd ?? channel?.duration ?? 0}
                clipOffset={channel?.clipOffset ?? 0}
                timelineDuration={timelineDuration}
                currentTime={currentTime}
                gridLines={gridLines}
                snapEnabled={snapEnabled}
                snapSeconds={snapSeconds}
                pan={channel?.pan ?? 0}
                volume={channel?.volume ?? 1.0}
                onArm={() => onArm(sectionId, type?.id)}
                onMute={() => onMute(sectionId, type?.id)}
                onSolo={() => onSolo(sectionId, type?.id)}
                onAudition={() => onAudition(sectionId, type?.id)}
                onPlayback={() => onPlayback(sectionId, type?.id)}
                onDelete={() => onDelete(sectionId, type?.id)}
                onPanChange={(val) =>
                  onPanChange?.(sectionId, type?.id, val)
                }
                onVolumeChange={(val) =>
                  onVolumeChange?.(sectionId, type?.id, val)
                }
                onClipChange={(updates, takeId) =>
                  onClipChange?.(sectionId, type?.id, updates, takeId)
                }
                onSelectTake={(takeId) => onSelectTake?.(channelKey, takeId)}
                onSeek={onSeek}
                onBeginEdit={onBeginEdit}
                disabled={disabled}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default ProToolsEditWindow;
